<template>
  <div class="payment-failed-container">
    <!-- Failed Icon with Animation -->
    <div class="failed-icon-container">
      <div class="failed-icon">
        <svg class="crossmark" viewBox="0 0 52 52">
          <circle class="crossmark-circle" cx="26" cy="26" r="25" fill="none"/>
          <path class="crossmark-cross" fill="none" d="m16 16l20 20m0-20l-20 20"/>
        </svg>
      </div>
    </div>

    <!-- Failed Message -->
    <div class="failed-message">
      <h1 class="failed-title">{{ titleText }}</h1>
      <p class="failed-subtitle">{{ subtitleText }}</p>
      <button type="button" class="failed-cta" @click="goToDashboard">
        Go to dashboard
      </button>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { readEsewaContext, clearEsewaContext } from '@/utilities/esewaPayment';

export default {
  name: 'PaymentFailed',
  data() {
    return {
      titleText: 'PAYMENT FAILED',
      subtitleText: 'We could not process your payment. Please try again.',
    };
  },
  async mounted() {
    await this.handleEsewaCallback();
  },
  methods: {
    async handleEsewaCallback() {
      const callbackToken = String(this.$route.query?.data || '').trim();
      const context = readEsewaContext();
      if (!callbackToken || !context?.referenceid || !context?.referencedata) {
        return;
      }
      try {
        const referencedata = {
          ...context.referencedata,
          callbackToken,
          propertycount: String(
            context.referencedata?.propertycount
              || context.propertycount
              || ''
          ),
          slotcount: String(
            context.referencedata?.slotcount
              || context.slotcount
              || ''
          ),
        };
        await apiService.esewaCallback(context.referenceid, referencedata);
      } catch (error) {
        console.error('Failed to process eSewa failed callback:', error);
      } finally {
        clearEsewaContext();
      }
    },
    goToDashboard() {
      this.$router.push('/dashboard');
    },
  },
}
</script>

<style scoped>
.payment-failed-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #fefefe 0%, #fef2f2 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* Failed Icon */
.failed-icon-container {
  margin-bottom: 40px;
  position: relative;
}

.failed-icon {
  width: 140px;
  height: 140px;
  margin: 0 auto;
  animation: shakeIn 0.8s ease-out;
  position: relative;
}

.failed-icon::before {
  content: '';
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  border-radius: 50%;
  opacity: 0.1;
  animation: pulse 2s infinite;
}

.crossmark {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 4px 12px rgba(239, 68, 68, 0.3));
}

.crossmark-circle {
  stroke: #ef4444;
  stroke-width: 4;
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  animation: stroke 0.8s cubic-bezier(0.65, 0, 0.45, 1) forwards;
  fill: rgba(239, 68, 68, 0.05);
}

.crossmark-cross {
  stroke: #ef4444;
  stroke-width: 4;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  animation: stroke 0.4s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards;
  stroke-linecap: round;
  stroke-linejoin: round;
}

@keyframes stroke {
  100% {
    stroke-dashoffset: 0;
  }
}

@keyframes shakeIn {
  0% {
    opacity: 0;
    transform: scale(0.3) rotate(-10deg);
  }
  25% {
    opacity: 1;
    transform: scale(1.1) rotate(5deg);
  }
  50% {
    transform: scale(0.9) rotate(-3deg);
  }
  75% {
    transform: scale(1.05) rotate(2deg);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.2;
  }
}

/* Failed Message */
.failed-message {
  text-align: center;
  max-width: 500px;
}

.failed-title {
  font-size: 3.5rem;
  font-weight: 800;
  color: #991b1b;
  margin: 0 0 16px 0;
  animation: fadeInUp 0.8s ease-out 0.5s both;
  letter-spacing: -0.02em;
  line-height: 1.1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.failed-subtitle {
  font-size: 1.25rem;
  color: #6b7280;
  margin: 0;
  animation: fadeInUp 0.8s ease-out 0.7s both;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.failed-cta {
  margin-top: 1.25rem;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  padding: 0.65rem 1.2rem;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(239, 68, 68, 0.24);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Professional Card Effect */
.payment-failed-container::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 600px;
  height: 60%;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 24px;
  box-shadow: 
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  z-index: -1;
}

/* Responsive Design */
@media (max-width: 768px) {
  .payment-failed-container {
    padding: 30px 16px;
  }

  .failed-title {
    font-size: 2.8rem;
  }

  .failed-subtitle {
    font-size: 1.1rem;
  }

  .failed-icon {
    width: 120px;
    height: 120px;
  }

  .failed-icon-container {
    margin-bottom: 32px;
  }
}

@media (max-width: 480px) {
  .payment-failed-container {
    padding: 24px 16px;
  }

  .failed-title {
    font-size: 2.2rem;
  }

  .failed-subtitle {
    font-size: 1rem;
  }

  .failed-icon {
    width: 100px;
    height: 100px;
  }

  .failed-icon-container {
    margin-bottom: 28px;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .failed-title {
    color: #000;
  }
  
  .failed-subtitle {
    color: #333;
  }
  
  .crossmark-circle,
  .crossmark-cross {
    stroke: #000;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .failed-icon,
  .failed-title,
  .failed-subtitle {
    animation: none;
  }
  
  .failed-icon::before {
    animation: none;
  }
}
</style>
